import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FileText, Plus } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { AUTH_FIBER_WARM } from '@/shared/ui-class';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import {
  projectModulesSeed,
  type EditorDocument,
} from '@/versions/v1/shared/project-modules-seed';
import {
  formatDocumentUpdated,
  nextDocumentId,
  saveHintFromEdit,
  sortDocumentsByUpdated,
} from './ProjectEditorScene.transform';

export function ProjectEditorScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const project = projectTileBySlug(projectId);
  const seed = projectModulesSeed(projectId);
  const now = useMemo(() => Date.now(), []);

  const [documents, setDocuments] = useState<EditorDocument[]>(() => seed.documents);
  const [selectedId, setSelectedId] = useState<string | null>(
    () => seed.documents[0]?.id ?? null,
  );
  const [editedAt, setEditedAt] = useState<number | null>(null);

  useEffect(() => {
    setDocuments(seed.documents);
    setSelectedId(seed.documents[0]?.id ?? null);
    setEditedAt(null);
  }, [projectId]);

  useEffect(() => {
    document.title = project
      ? `Editor · ${project.name} · Foundation`
      : 'Editor · Foundation';
  }, [project]);

  const sorted = useMemo(() => sortDocumentsByUpdated(documents), [documents]);
  const selected = documents.find((doc) => doc.id === selectedId) ?? null;

  const handleContentChange = (content: string) => {
    if (!selectedId) return;
    const ts = Date.now();
    setEditedAt(ts);
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.id === selectedId
          ? { ...doc, content, updatedAt: ts }
          : doc,
      ),
    );
  };

  const handleNewDocument = () => {
    const ts = Date.now();
    const doc: EditorDocument = {
      id: nextDocumentId(documents),
      title: 'Untitled document',
      updatedAt: ts,
      content: '',
    };
    setDocuments((prev) => [doc, ...prev]);
    setSelectedId(doc.id);
    setEditedAt(null);
  };

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Project not found" />
          <Link to="/projects" className="text-sm text-accent-600">
            Back to portfolio
          </Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  return (
    <SurfacePageLayout width="full">
      <Commentable name="ProjectEditorScene">
        <SurfacePageBody width="full" className="space-y-6">
          <SurfacePageHeader
            kicker={project.name}
            title="Editor"
            description="Collaborative documents and canvases scoped to the project."
            actions={<HelpAnchor doc="workspace/project-editor" label="Editor" />}
          />

          {documents.length === 0 ? (
            <EmptyState
              icon={<FileText className="mx-auto h-8 w-8 text-zinc-400" />}
              title="No documents yet"
              description="Start a document to capture notes, specs, and drafts in this project."
              action={
                <button
                  type="button"
                  className="btn-primary inline-flex h-9 items-center gap-1.5 px-4 text-sm"
                  onClick={handleNewDocument}
                >
                  <Plus className="h-3.5 w-3.5" />
                  New document
                </button>
              }
            />
          ) : (
            <div className="flex min-h-[28rem] flex-col gap-4 lg:flex-row">
              <aside className="w-full flex-shrink-0 lg:w-64">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h2 className="text-xs font-medium uppercase tracking-wide text-muted">
                    Documents
                  </h2>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs font-medium text-accent-600"
                    onClick={handleNewDocument}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    New
                  </button>
                </div>
                <ul className="divide-y divide-[var(--glass-border)] rounded-xl border border-[var(--glass-border)] bg-[var(--glass-surface)]">
                  {sorted.map((doc) => (
                    <li key={doc.id}>
                      <button
                        type="button"
                        className={cn(
                          'w-full px-4 py-3 text-left transition-colors',
                          doc.id === selectedId
                            ? 'bg-[color-mix(in_srgb,var(--color-accent-500)_10%,transparent)]'
                            : 'hover:bg-zinc-900/[0.03] dark:hover:bg-white/[0.03]',
                        )}
                        onClick={() => {
                          setSelectedId(doc.id);
                          setEditedAt(null);
                        }}
                      >
                        <p className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
                          {doc.title}
                        </p>
                        <p className="mt-0.5 text-xs text-muted">
                          {formatDocumentUpdated(doc.updatedAt, now)}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              </aside>

              <div className="min-w-0 flex-1">
                {selected ? (
                  <div className="space-y-2">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                        {selected.title}
                      </h3>
                      <span className="text-xs text-muted">
                        {saveHintFromEdit(editedAt, now)}
                      </span>
                    </div>
                    <div
                      className={cn(
                        'rounded-xl border border-[var(--glass-border)] shadow-sm',
                        AUTH_FIBER_WARM,
                      )}
                    >
                      <textarea
                        value={selected.content}
                        onChange={(e) => handleContentChange(e.target.value)}
                        className={cn(
                          'min-h-[24rem] w-full resize-y bg-transparent px-6 py-5',
                          'font-mono text-sm leading-relaxed text-zinc-800',
                          'outline-none dark:text-zinc-100',
                        )}
                        spellCheck={false}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
